import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FaBan,
  FaCalendarDay,
  FaCheckCircle,
  FaEdit,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaPlus,
  FaTrash,
  FaUndo,
  FaWallet,
} from "react-icons/fa";
import { useConfirm, useNotify } from "../../components/ConfirmModal";
import FinancePageHead, { FinanceStats, FinanceTabs } from "../../components/FinancePageHead";
import { ResultsMobileCard, ResultsMobileList } from "../../components/ResultsMobile";
import { FINANCE_SECTIONS, financeStatusLabel, financeStatusTone } from "../../utils/financeTerms";
import payablesApi, { loadPayablesOptions, payablesError } from "./payablesApi";
import { BillModal, CancelBillModal, PayBillModal, VendorModal, formatDate, formatMoney } from "./PayablesForms";
import "../../assets/css/DigixLeads.css";
import "../../assets/css/AccountsReceivable.css";
import "../../assets/css/FinanceManagement.css";

const TAB_BILLS = "bills";
const TAB_PAYMENTS = "payments-made";
const TAB_VENDORS = "vendors";

const BILL_FILTERS = [
  { key: "open", label: "To pay" },
  { key: "overdue", label: "Overdue" },
  { key: "draft", label: "Drafts" },
  { key: "paid", label: "Paid" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
];

const EMPTY_OPTIONS = { modes: [], accounts: [], currencies: [] };

function StatusBadge({ record }) {
  const label = financeStatusLabel(record);
  return <span className={`finance-tenants-status finance-tenants-status--${financeStatusTone(label)}`}>{label}</span>;
}

function paymentTone(status) {
  if (status === "Completed") return "success";
  if (status === "Pending") return "warning";
  return "danger";
}

const money = (amount, code) => `${code ? `${code} ` : ""}${formatMoney(amount)}`;

function BillsTab({ vendors, options, onChanged, refreshKey }) {
  const confirm = useConfirm();
  const notify = useNotify();
  const [filter, setFilter] = useState("open");
  const [vendorId, setVendorId] = useState("");
  const [search, setSearch] = useState("");
  const [bills, setBills] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    try {
      const rows = await payablesApi.bills({
        status: filter,
        vendor_id: vendorId || undefined,
        search: search.trim() || undefined,
      });
      setBills(Array.isArray(rows) ? rows : []);
      setError("");
    } catch (err) {
      setError(payablesError(err, "Could not load bills"));
      setBills([]);
    }
  }, [filter, vendorId, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, refreshKey]);

  const done = () => {
    setEditing(null);
    setPaying(null);
    setCancelling(null);
    load();
    onChanged();
  };

  const removeDraft = async (bill) => {
    const ok = await confirm({ title: "Delete draft", message: `Delete draft bill ${bill.Bill_No || `#${bill.Bill_Id}`}?`, confirmLabel: "Delete" });
    if (!ok) return;
    try {
      await payablesApi.deleteBill(bill.Bill_Id);
      notify.success("Draft deleted");
      done();
    } catch (err) {
      notify.error(payablesError(err, "Could not delete the draft"));
    }
  };

  const canPay = (b) => b.Status === "Unpaid" || b.Status === "Partially Paid";
  const totals = useMemo(
    () => (bills || []).reduce((acc, b) => ({ total: acc.total + Number(b.Total_Amount || 0), balance: acc.balance + Number(b.Balance_Amount || 0) }), { total: 0, balance: 0 }),
    [bills]
  );

  const actions = (b, card) => {
    const cls = card ? "co-maction" : "accounts-payable-action-btn";
    const label = (t) => (card ? <span>{t}</span> : null);
    return (
      <div className={card ? "co-mcard__action-grid" : "accounts-payable-actions-cell"}>
        {canPay(b) ? (
          <button type="button" className={`${cls}${card ? " co-maction--pay" : ""}`} title="Record a payment" aria-label="Record a payment" onClick={() => setPaying(b)}>
            <FaMoneyBillWave size={card ? 14 : undefined} />
            {label("Pay")}
          </button>
        ) : null}
        <button type="button" className={cls} title="Open bill" aria-label="Open bill" onClick={() => setEditing(b.Bill_Id)}>
          <FaEdit size={card ? 14 : undefined} />
          {label("Open")}
        </button>
        {b.Status === "Draft" ? (
          <button type="button" className={cls} title="Delete draft" aria-label="Delete draft" onClick={() => removeDraft(b)}>
            <FaTrash size={card ? 14 : undefined} />
            {label("Delete")}
          </button>
        ) : b.Status !== "Cancelled" && Number(b.Paid_Amount) <= 0 ? (
          <button type="button" className={cls} title="Cancel bill" aria-label="Cancel bill" onClick={() => setCancelling(b)}>
            <FaBan size={card ? 14 : undefined} />
            {label("Cancel")}
          </button>
        ) : null}
      </div>
    );
  };

  const emptyLabel = filter === "open" ? "Nothing to pay. You are all caught up." : "No bills here.";

  return (
    <div className="accounts-payable">
      <div className="finance-filter-bar">
        {BILL_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`finance-tab-pill${filter === f.key ? " is-active" : ""}`}
            style={{ display: "inline-block" }}
            aria-pressed={filter === f.key}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="search-actions-row accounts-payable-search-actions-row">
        <div className="search-filters-row accounts-payable-toolbar-right">
          <input
            type="text"
            className="search-filter-input"
            placeholder="Search bill no., vendor, category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search bills"
          />
          <select className="search-filter-input search-filter-select" value={vendorId} onChange={(e) => setVendorId(e.target.value)} aria-label="Vendor">
            <option value="">All vendors</option>
            {vendors.map((v) => (
              <option key={v.Vendor_Id} value={String(v.Vendor_Id)}>
                {v.Name}
              </option>
            ))}
          </select>
        </div>
        <div className="header-actions">
          <button type="button" className="btn-primary btn-add-lead" onClick={() => setEditing("new")} disabled={!vendors.some((v) => v.Status === "Active")} title={vendors.length ? "Add a bill you received" : "Add a vendor first"}>
            <FaPlus size={11} /> New bill
          </button>
        </div>
      </div>

      {error ? <div className="accounts-payable-alert">{error}</div> : null}

      <div className="accounts-payable-table-wrap co-desktop-table">
        <table className="accounts-payable-table finance-tenants-table">
          <thead>
            <tr>
              <th className="accounts-payable-sticky-first">Bill no.</th>
              <th>Vendor</th>
              <th>Category</th>
              <th>Bill date</th>
              <th>Pay by</th>
              <th className="finance-tenants-num">Total</th>
              <th className="finance-tenants-num">Left to pay</th>
              <th>Status</th>
              <th className="accounts-payable-sticky-last finance-tenants-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!bills ? (
              <tr>
                <td colSpan={9} className="accounts-payable-empty">Loading bills...</td>
              </tr>
            ) : bills.length === 0 ? (
              <tr>
                <td colSpan={9} className="accounts-payable-empty">{emptyLabel}</td>
              </tr>
            ) : (
              bills.map((b) => (
                <tr key={b.Bill_Id}>
                  <td className="accounts-payable-sticky-first">
                    <span className="table-name-link" role="button" tabIndex={0} onClick={() => setEditing(b.Bill_Id)} onKeyDown={(e) => e.key === "Enter" && setEditing(b.Bill_Id)}>
                      {b.Bill_No || `#${b.Bill_Id}`}
                    </span>
                  </td>
                  <td>{b.Vendor_Name || "—"}</td>
                  <td>{b.Category || "—"}</td>
                  <td>{formatDate(b.Bill_Date)}</td>
                  <td className={b.Is_Overdue ? "finance-aging-late" : undefined}>
                    {formatDate(b.Due_Date)}
                    {b.Is_Overdue ? <div className="finance-form-hint">{b.Days_Overdue} days late</div> : null}
                  </td>
                  <td className="finance-tenants-num">{money(b.Total_Amount, b.Currency_Code)}</td>
                  <td className={`finance-tenants-num${Number(b.Balance_Amount) > 0 && canPay(b) ? " finance-tenants-due" : ""}`}>
                    {money(b.Balance_Amount, b.Currency_Code)}
                  </td>
                  <td>
                    <StatusBadge record={b} />
                  </td>
                  <td className="accounts-payable-sticky-last finance-tenants-actions-col">{actions(b, false)}</td>
                </tr>
              ))
            )}
          </tbody>
          {bills && bills.length > 0 ? (
            <tfoot>
              <tr>
                <td className="accounts-payable-sticky-first">
                  {bills.length} bill{bills.length === 1 ? "" : "s"}
                </td>
                <td colSpan={4} />
                <td className="finance-tenants-num">{formatMoney(totals.total)}</td>
                <td className="finance-tenants-num">{formatMoney(totals.balance)}</td>
                <td />
                <td className="accounts-payable-sticky-last" />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      <ResultsMobileList loading={!bills} loadingLabel="Loading bills..." emptyLabel={emptyLabel} className="finance-tenants-mobile">
        {(bills || []).map((b) => {
          const label = financeStatusLabel(b);
          return (
            <ResultsMobileCard
              key={b.Bill_Id}
              title={b.Bill_No || `#${b.Bill_Id}`}
              subtitle={b.Vendor_Name}
              tone={label === "Overdue" ? "danger" : undefined}
              meta={[{ label, tone: financeStatusTone(label) }]}
              fields={[
                { label: "Pay by", value: formatDate(b.Due_Date) },
                { label: "Total", value: money(b.Total_Amount, b.Currency_Code) },
                { label: "Left to pay", value: money(b.Balance_Amount, b.Currency_Code) },
                { label: "Category", value: b.Category || "—" },
              ]}
              onOpen={() => setEditing(b.Bill_Id)}
              actions={actions(b, true)}
            />
          );
        })}
      </ResultsMobileList>

      {editing ? (
        <BillModal
          billId={editing === "new" ? null : editing}
          vendors={vendors}
          options={options}
          onClose={() => setEditing(null)}
          onSaved={done}
        />
      ) : null}
      {paying ? <PayBillModal bill={paying} options={options} onClose={() => setPaying(null)} onSaved={done} /> : null}
      {cancelling ? <CancelBillModal bill={cancelling} onClose={() => setCancelling(null)} onSaved={done} /> : null}
    </div>
  );
}

function PaymentsMadeTab({ vendors, onChanged, refreshKey }) {
  const confirm = useConfirm();
  const notify = useNotify();
  const [vendorId, setVendorId] = useState("");
  const [payments, setPayments] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const rows = await payablesApi.payments({ vendor_id: vendorId || undefined });
      setPayments(Array.isArray(rows) ? rows : []);
      setError("");
    } catch (err) {
      setError(payablesError(err, "Could not load payments"));
      setPayments([]);
    }
  }, [vendorId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const undo = async (p) => {
    const ok = await confirm({
      title: "Cancel payment",
      message: `Cancel the payment of ${formatMoney(p.Amount)} to ${p.Vendor_Name}? The bill goes back to unpaid for this amount.`,
      confirmLabel: "Cancel payment",
    });
    if (!ok) return;
    try {
      await payablesApi.cancelPayment(p.Payment_Id);
      notify.success("Payment cancelled");
      load();
      onChanged();
    } catch (err) {
      notify.error(payablesError(err, "Could not cancel the payment"));
    }
  };

  const total = (payments || []).filter((p) => p.Payment_Status === "Completed").reduce((s, p) => s + Number(p.Amount || 0), 0);

  return (
    <div className="accounts-payable">
      <div className="search-actions-row accounts-payable-search-actions-row">
        <div className="search-filters-row accounts-payable-toolbar-right">
          <select className="search-filter-input search-filter-select" value={vendorId} onChange={(e) => setVendorId(e.target.value)} aria-label="Vendor">
            <option value="">All vendors</option>
            {vendors.map((v) => (
              <option key={v.Vendor_Id} value={String(v.Vendor_Id)}>
                {v.Name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {error ? <div className="accounts-payable-alert">{error}</div> : null}
      <div className="accounts-payable-table-wrap co-desktop-table">
        <table className="accounts-payable-table finance-tenants-table">
          <thead>
            <tr>
              <th className="accounts-payable-sticky-first">Paid on</th>
              <th>Vendor</th>
              <th>Bill no.</th>
              <th className="finance-tenants-num">Amount</th>
              <th>Paid from</th>
              <th>Mode</th>
              <th>Reference</th>
              <th>Status</th>
              <th className="accounts-payable-sticky-last finance-tenants-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!payments ? (
              <tr>
                <td colSpan={9} className="accounts-payable-empty">Loading payments...</td>
              </tr>
            ) : payments.length === 0 ? (
              <tr>
                <td colSpan={9} className="accounts-payable-empty">No payments made yet.</td>
              </tr>
            ) : (
              payments.map((p) => (
                <tr key={p.Payment_Id}>
                  <td className="accounts-payable-sticky-first">{formatDate(p.Payment_Date)}</td>
                  <td>{p.Vendor_Name || "—"}</td>
                  <td>{p.Bill_No || `#${p.Bill_Id}`}</td>
                  <td className="finance-tenants-num">{money(p.Amount, p.Currency_Code)}</td>
                  <td>{p.Company_Account_Name || "—"}</td>
                  <td>{p.Payment_Mode_Name || "—"}</td>
                  <td>{p.Transaction_Ref || "—"}</td>
                  <td>
                    <span className={`finance-tenants-status finance-tenants-status--${paymentTone(p.Payment_Status)}`}>{p.Payment_Status}</span>
                  </td>
                  <td className="accounts-payable-sticky-last finance-tenants-actions-col">
                    {p.Payment_Status !== "Cancelled" ? (
                      <button type="button" className="accounts-payable-action-btn" title="Cancel payment" aria-label="Cancel payment" onClick={() => undo(p)}>
                        <FaUndo />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {payments && payments.length > 0 ? (
            <tfoot>
              <tr>
                <td className="accounts-payable-sticky-first">Total paid</td>
                <td colSpan={2} />
                <td className="finance-tenants-num">{formatMoney(total)}</td>
                <td colSpan={4} />
                <td className="accounts-payable-sticky-last" />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <ResultsMobileList loading={!payments} loadingLabel="Loading payments..." emptyLabel="No payments made yet." className="finance-tenants-mobile">
        {(payments || []).map((p) => (
          <ResultsMobileCard
            key={p.Payment_Id}
            title={money(p.Amount, p.Currency_Code)}
            titleIcon={<FaCheckCircle />}
            subtitle={p.Vendor_Name}
            meta={[{ label: p.Payment_Status, tone: paymentTone(p.Payment_Status) }]}
            fields={[
              { label: "Paid on", value: formatDate(p.Payment_Date) },
              { label: "Bill no.", value: p.Bill_No || `#${p.Bill_Id}` },
              { label: "Paid from", value: p.Company_Account_Name || "—" },
              { label: "Reference", value: p.Transaction_Ref || "—" },
            ]}
            actions={
              p.Payment_Status !== "Cancelled" ? (
                <div className="co-mcard__action-grid">
                  <button type="button" className="co-maction" onClick={() => undo(p)}>
                    <FaUndo size={14} />
                    <span>Cancel</span>
                  </button>
                </div>
              ) : null
            }
          />
        ))}
      </ResultsMobileList>
    </div>
  );
}

function VendorsTab({ vendors, loading, onChanged }) {
  const confirm = useConfirm();
  const notify = useNotify();
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("Active");

  const rows = vendors.filter((v) => {
    if (status && v.Status !== status) return false;
    const q = search.trim().toLowerCase();
    return !q || [v.Name, v.Email, v.GSTIN, v.Default_Category].filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  const remove = async (v) => {
    const ok = await confirm({ title: "Delete vendor", message: `Delete ${v.Name}?`, confirmLabel: "Delete" });
    if (!ok) return;
    try {
      await payablesApi.deleteVendor(v.Vendor_Id);
      notify.success("Vendor deleted");
      onChanged();
    } catch (err) {
      notify.error(payablesError(err, "Could not delete the vendor"));
    }
  };

  return (
    <div className="accounts-payable">
      <div className="search-actions-row accounts-payable-search-actions-row">
        <div className="search-filters-row accounts-payable-toolbar-right">
          <input type="text" className="search-filter-input" placeholder="Search vendor, email, GSTIN..." value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search vendors" />
          <select className="search-filter-input search-filter-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            <option value="">All status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        <div className="header-actions">
          <button type="button" className="btn-primary btn-add-lead" onClick={() => setEditing("new")}>
            <FaPlus size={11} /> New vendor
          </button>
        </div>
      </div>
      <div className="accounts-payable-table-wrap co-desktop-table">
        <table className="accounts-payable-table finance-tenants-table">
          <thead>
            <tr>
              <th className="accounts-payable-sticky-first">Vendor</th>
              <th>Category</th>
              <th>Contact</th>
              <th>Pays within</th>
              <th className="finance-tenants-num">Open bills</th>
              <th className="finance-tenants-num">We owe them</th>
              <th>Status</th>
              <th className="accounts-payable-sticky-last finance-tenants-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="accounts-payable-empty">Loading vendors...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="accounts-payable-empty">
                  {vendors.length ? "No vendors match these filters." : "Add the companies that send you bills (hosting, software, rent...)."}
                </td>
              </tr>
            ) : (
              rows.map((v) => (
                <tr key={v.Vendor_Id}>
                  <td className="accounts-payable-sticky-first">
                    <span className="table-name-link" role="button" tabIndex={0} onClick={() => setEditing(v)} onKeyDown={(e) => e.key === "Enter" && setEditing(v)}>
                      {v.Name}
                    </span>
                  </td>
                  <td>{v.Default_Category || "—"}</td>
                  <td>{[v.Contact_Person, v.Email, v.Phone].filter(Boolean).join(" · ") || "—"}</td>
                  <td>{v.Payment_Terms_Days != null ? `${v.Payment_Terms_Days} days` : "—"}</td>
                  <td className="finance-tenants-num">{v.Open_Bills}</td>
                  <td className={`finance-tenants-num${Number(v.Balance_Amount) > 0 ? " finance-tenants-due" : ""}`}>{formatMoney(v.Balance_Amount)}</td>
                  <td>
                    <span className={`finance-tenants-status finance-tenants-status--${v.Status === "Active" ? "success" : "neutral"}`}>{v.Status}</span>
                  </td>
                  <td className="accounts-payable-sticky-last finance-tenants-actions-col">
                    <div className="accounts-payable-actions-cell">
                      <button type="button" className="accounts-payable-action-btn" title="Edit vendor" aria-label="Edit vendor" onClick={() => setEditing(v)}>
                        <FaEdit />
                      </button>
                      <button type="button" className="accounts-payable-action-btn" title="Delete vendor" aria-label="Delete vendor" onClick={() => remove(v)}>
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <ResultsMobileList loading={loading} loadingLabel="Loading vendors..." emptyLabel="No vendors yet." className="finance-tenants-mobile">
        {!loading &&
          rows.map((v) => (
            <ResultsMobileCard
              key={v.Vendor_Id}
              title={v.Name}
              subtitle={v.Default_Category}
              meta={[{ label: v.Status, tone: v.Status === "Active" ? "success" : "neutral" }]}
              fields={[
                { label: "Open bills", value: v.Open_Bills },
                { label: "We owe them", value: formatMoney(v.Balance_Amount) },
                { label: "Pays within", value: v.Payment_Terms_Days != null ? `${v.Payment_Terms_Days} days` : "—" },
              ]}
              onOpen={() => setEditing(v)}
            />
          ))}
      </ResultsMobileList>
      {editing ? (
        <VendorModal
          vendor={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

/** Finance > Payables: bills Cloud Office has to pay its vendors, and what has been paid. */
function FinancePayablesPage() {
  const [params, setParams] = useSearchParams();
  const tab = [TAB_BILLS, TAB_PAYMENTS, TAB_VENDORS].includes(params.get("tab")) ? params.get("tab") : TAB_BILLS;
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState("");

  const loadShared = useCallback(async () => {
    try {
      const [s, v] = await Promise.all([payablesApi.summary(), payablesApi.vendors()]);
      setSummary(s);
      setVendors(Array.isArray(v) ? v : []);
      setError("");
    } catch (err) {
      setError(payablesError(err, "Could not load payables"));
    } finally {
      setVendorsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShared();
    loadPayablesOptions().then(setOptions).catch(() => setOptions(EMPTY_OPTIONS));
  }, [loadShared]);

  const changed = () => {
    loadShared();
    setRefreshKey((k) => k + 1);
  };

  const setTab = (key) => setParams({ tab: key }, { replace: true });
  const agingMax = Math.max(1, ...(summary?.aging || []).map((a) => Number(a.amount || 0)));

  return (
    <div className="digixleads-container finance-management-page">
      <FinancePageHead section={FINANCE_SECTIONS.payables} hint="Bills you need to pay your vendors, and what you have already paid" />
      <div className="main-card">
        {error ? <div className="accounts-payable-alert">{error}</div> : null}
        <FinanceStats
          items={[
            { key: "to-pay", label: summary?.open_count ? `To pay (${summary.open_count} bills)` : "To pay", value: summary ? formatMoney(summary.to_pay) : "—", icon: FaWallet, tone: "info", onClick: () => setTab(TAB_BILLS) },
            { key: "overdue", label: summary?.overdue_count ? `Overdue (${summary.overdue_count})` : "Overdue", value: summary ? formatMoney(summary.overdue) : "—", icon: FaExclamationTriangle, tone: "danger", onClick: () => setTab(TAB_BILLS) },
            { key: "week", label: "Due this week", value: summary ? formatMoney(summary.due_this_week) : "—", icon: FaCalendarDay, tone: "warning", onClick: () => setTab(TAB_BILLS) },
            { key: "paid", label: "Paid this month", value: summary ? formatMoney(summary.paid_this_month) : "—", icon: FaCheckCircle, tone: "success", onClick: () => setTab(TAB_PAYMENTS) },
          ]}
        />
        {summary && Number(summary.overdue) > 0 ? (
          <table className="finance-items-table" style={{ marginBottom: 14 }} aria-label="How late unpaid bills are">
            <tbody>
              {summary.aging.map((a) => (
                <tr key={a.key}>
                  <td style={{ width: 140 }}>{a.label}</td>
                  <td>
                    <div style={{ height: 8, borderRadius: 4, background: "#eef2f7" }}>
                      <div
                        style={{
                          height: 8,
                          borderRadius: 4,
                          width: `${(Number(a.amount) / agingMax) * 100}%`,
                          background: a.key === "current" ? "#60a5fa" : "#f87171",
                        }}
                      />
                    </div>
                  </td>
                  <td className="finance-tenants-num" style={{ width: 120, textAlign: "right" }}>{formatMoney(a.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        <FinanceTabs
          tabs={[
            { key: TAB_BILLS, label: "Bills to Pay", count: summary?.open_count },
            { key: TAB_PAYMENTS, label: "Payments Made" },
            { key: TAB_VENDORS, label: "Vendors", count: summary?.active_vendors },
          ]}
          active={tab}
          onChange={setTab}
          ariaLabel="Payables section"
        />
        <div className="finance-management-tab-body">
          {tab === TAB_BILLS && <BillsTab vendors={vendors} options={options} onChanged={changed} refreshKey={refreshKey} />}
          {tab === TAB_PAYMENTS && <PaymentsMadeTab vendors={vendors} onChanged={changed} refreshKey={refreshKey} />}
          {tab === TAB_VENDORS && <VendorsTab vendors={vendors} loading={vendorsLoading} onChanged={changed} />}
        </div>
      </div>
    </div>
  );
}

export default FinancePayablesPage;
